import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  Button,
  Drawer,
  DrawerSection,
  FailedCallout,
  ReasonField,
  Select,
  TextInput,
  useReason,
  useToast,
  useCommandMutation,
} from "@amber/ui";
import {
  NAME_TYPES,
  NAME_TYPE_LABELS,
  ORGANISATION_KINDS,
  ORGANISATION_KIND_LABELS,
  ROLE_KIND_LABELS,
  type ProfileDetail,
  type Role,
  type RoleEnd,
} from "../../contracts/index";
import { useProfileInvalidation } from "../invalidation";
import { profileCommands } from "../queries";

interface RoleRow {
  id?: string;
  title: string;
  kind: Role["kind"];
  org: string;
  orgKind: (typeof ORGANISATION_KINDS)[number];
  start: string;
  endState: RoleEnd["state"];
  endDate: string;
  evidence: string;
}

interface NameRow {
  value: string;
  type: (typeof NAME_TYPES)[number];
  validFrom: string;
  validTo: string;
  evidence: string;
}

function RolesSection({ roles, setRoles }: { roles: RoleRow[]; setRoles: (next: RoleRow[]) => void }) {
  const setRole = (i: number, patch: Partial<RoleRow>) => {
    setRoles(roles.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };
  return (
    <DrawerSection title="Roles and affiliations">
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {roles.map((r, i) => (
          <li
            key={r.id ?? `new-${String(i)}`}
            className="grid grid-cols-2 gap-1.5 rounded-lg border border-divider p-2"
          >
            <TextInput
              aria-label="Role"
              placeholder="Role or title"
              value={r.title}
              onChange={(e) => {
                setRole(i, { title: e.target.value });
              }}
              className="col-span-2"
            />
            <Select
              aria-label="Kind"
              value={r.kind}
              onChange={(e) => {
                setRole(i, { kind: e.target.value as Role["kind"] });
              }}
            >
              {Object.entries(ROLE_KIND_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Organisation kind"
              value={r.orgKind}
              onChange={(e) => {
                setRole(i, { orgKind: e.target.value as RoleRow["orgKind"] });
              }}
            >
              {ORGANISATION_KINDS.map((k) => (
                <option key={k} value={k}>
                  {ORGANISATION_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
            <TextInput
              aria-label="Organisation"
              placeholder="Organisation (optional)"
              value={r.org}
              onChange={(e) => {
                setRole(i, { org: e.target.value });
              }}
              className="col-span-2"
            />
            <TextInput
              aria-label="From"
              placeholder="From (2019, 2019-03 or a date)"
              value={r.start}
              onChange={(e) => {
                setRole(i, { start: e.target.value });
              }}
            />
            <Select
              aria-label="End"
              value={r.endState}
              onChange={(e) => {
                setRole(i, { endState: e.target.value as RoleEnd["state"] });
              }}
            >
              <option value="ongoing">Ongoing</option>
              <option value="unknown">End unknown</option>
              <option value="ended">Ended</option>
            </Select>
            {r.endState === "ended" ? (
              <TextInput
                aria-label="Ended"
                placeholder="Ended (year, month or date)"
                value={r.endDate}
                onChange={(e) => {
                  setRole(i, { endDate: e.target.value });
                }}
                className="col-span-2"
              />
            ) : null}
            <TextInput
              aria-label="Evidence"
              placeholder="Evidence"
              value={r.evidence}
              onChange={(e) => {
                setRole(i, { evidence: e.target.value });
              }}
              className="col-span-2"
            />
            <Button
              size="xs"
              className="col-span-2 justify-self-end"
              onClick={() => {
                setRoles(roles.filter((_, j) => j !== i));
              }}
            >
              <Trash2 aria-hidden="true" /> Remove
            </Button>
          </li>
        ))}
      </ul>
      <Button
        size="sm"
        className="mt-2"
        onClick={() => {
          setRoles([
            ...roles,
            {
              title: "",
              kind: "office",
              org: "",
              orgKind: "office",
              start: "",
              endState: "ongoing",
              endDate: "",
              evidence: "",
            },
          ]);
        }}
      >
        <Plus aria-hidden="true" /> Add role
      </Button>
    </DrawerSection>
  );
}

function NamesSection({ names, setNames }: { names: NameRow[]; setNames: (next: NameRow[]) => void }) {
  const setName = (i: number, patch: Partial<NameRow>) => {
    setNames(names.map((n, j) => (j === i ? { ...n, ...patch } : n)));
  };
  return (
    <DrawerSection title="Names and aliases">
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {names.map((n, i) => (
          <li key={`name-${String(i)}`} className="grid grid-cols-2 gap-1.5 rounded-lg border border-divider p-2">
            <TextInput
              aria-label="Name"
              placeholder="Name"
              value={n.value}
              onChange={(e) => {
                setName(i, { value: e.target.value });
              }}
            />
            <Select
              aria-label="Name type"
              value={n.type}
              onChange={(e) => {
                setName(i, { type: e.target.value as NameRow["type"] });
              }}
            >
              {NAME_TYPES.map((t) => (
                <option key={t} value={t}>
                  {NAME_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
            <TextInput
              aria-label="Valid from"
              placeholder="Valid from (optional)"
              value={n.validFrom}
              onChange={(e) => {
                setName(i, { validFrom: e.target.value });
              }}
            />
            <TextInput
              aria-label="Valid to"
              placeholder="Valid to (optional)"
              value={n.validTo}
              onChange={(e) => {
                setName(i, { validTo: e.target.value });
              }}
            />
            <TextInput
              aria-label="Evidence"
              placeholder="Evidence"
              value={n.evidence}
              onChange={(e) => {
                setName(i, { evidence: e.target.value });
              }}
              className="col-span-2"
            />
            <Button
              size="xs"
              className="col-span-2 justify-self-end"
              onClick={() => {
                setNames(names.filter((_, j) => j !== i));
              }}
            >
              <Trash2 aria-hidden="true" /> Remove
            </Button>
          </li>
        ))}
      </ul>
      <Button
        size="sm"
        className="mt-2"
        onClick={() => {
          setNames([...names, { value: "", type: "alias", validFrom: "", validTo: "", evidence: "" }]);
        }}
      >
        <Plus aria-hidden="true" /> Add name
      </Button>
    </DrawerSection>
  );
}

export function EditIdentityPanel({ profile, onClose }: { profile: ProfileDetail; onClose: () => void }) {
  const toast = useToast();
  const invalidation = useProfileInvalidation();
  const [roles, setRoles] = useState<RoleRow[]>(() =>
    profile.roles.map((r) => ({
      id: r.id,
      title: r.title,
      kind: r.kind,
      org: r.organisation?.name ?? "",
      orgKind: r.organisation?.kind ?? "party",
      start: r.start ?? "",
      endState: r.end.state,
      endDate: r.end.state === "ended" ? r.end.date : "",
      evidence: r.evidence,
    })),
  );
  const [names, setNames] = useState<NameRow[]>(() =>
    profile.names.map((n) => ({
      value: n.value,
      type: n.type,
      validFrom: n.valid_from ?? "",
      validTo: n.valid_to ?? "",
      evidence: n.evidence,
    })),
  );
  const reason = useReason();
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      profileCommands.editIdentity(
        profile.id,
        {
          base_revision: profile.revision,
          reason: text,
          names: names.map((n) => ({
            value: n.value,
            type: n.type,
            valid_from: n.validFrom || null,
            valid_to: n.validTo || null,
            evidence: n.evidence,
          })),
          roles: roles.map((r) => ({
            ...(r.id ? { id: r.id } : {}),
            title: r.title,
            kind: r.kind,
            organisation: r.org.trim() ? { name: r.org.trim(), kind: r.orgKind } : null,
            start: r.start || null,
            end: r.endState === "ended" ? { state: "ended", date: r.endDate } : { state: r.endState },
            evidence: r.evidence,
          })),
        },
        csrf,
      ),
    ...invalidation,
    onSuccess: () => {
      toast.show("Identity saved");
      onClose();
    },
  });
  return (
    <Drawer
      title="Edit identity"
      subtitle={profile.name}
      onClose={onClose}
      footer={
        <span className="flex items-center justify-end gap-2">
          <span className="mr-auto text-md text-muted">Nothing changes until you confirm.</span>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            form="identity-form"
            aria-busy={mutation.isPending || undefined}
          >
            {mutation.isError ? "Try again" : "Save identity"}
          </Button>
        </span>
      }
    >
      <form
        id="identity-form"
        noValidate
        className="flex flex-col gap-4 text-ui"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.submit()) mutation.mutate(reason.trimmed);
        }}
      >
        <RolesSection roles={roles} setRoles={setRoles} />
        <NamesSection names={names} setNames={setNames} />
        <DrawerSection title="What happens">
          <p className="m-0 text-md text-muted">
            Speaker matching uses the new names from now on; existing attributions stay. Edited values are marked as
            manual and protected from Wikipedia refreshes, and the edit counts as an identity check (last verified
            becomes today).
          </p>
        </DrawerSection>
        <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
        <FailedCallout error={mutation.error} className="" />
      </form>
    </Drawer>
  );
}
